import React, { useEffect, useState, useCallback } from 'react';
import { MailCheck, Loader2, Check, X, RefreshCw, Users, Inbox, LifeBuoy } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import PestanasModulo from '../../../components/common/PestanasModulo';
import { CORREOS_TABS } from '../pestanas';
import { adminCampaignsApi, type PendingEmailCampaign } from '../../../api/adminCampaignsApi';
import { useConfirmDialog } from '../../../components/common/ConfirmDialog';

// Panel super_admin: solicitudes de campañas de CORREO pendientes de aprobar.
// El dueño solicita (schedule); aquí las revisamos y aprobamos. Sin aprobar NO salen.
const AdminEmailRequestsPage: React.FC = () => {
  const { confirm, dialog } = useConfirmDialog();
  const { currentUser } = useAuth();
  const [rows, setRows] = useState<PendingEmailCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await adminCampaignsApi.listPendingEmail()); }
    catch { toast.error('No se pudieron cargar las solicitudes'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (r: PendingEmailCampaign) => {
    const ok = await confirm({
      title: `¿Aprobar la campaña "${r.name}"?`,
      message: `De ${r.tenant}. Saldrá a ${r.total_queued} destinatarios (si el canal está activo).`,
      confirmLabel: 'Aprobar campaña',
    });
    if (!ok) return;
    setBusyId(r.id);
    try { await adminCampaignsApi.approve(r.id); toast.success('Aprobada'); setRows(prev => prev.filter(x => x.id !== r.id)); }
    catch { toast.error('No se pudo aprobar'); }
    finally { setBusyId(null); }
  };

  const reject = async (r: PendingEmailCampaign) => {
    const ok = await confirm({
      title: `¿Mantener "${r.name}" en revisión?`,
      message: 'Quita la aprobación: la campaña no envía hasta que se apruebe de nuevo.',
      confirmLabel: 'Quitar aprobación',
    });
    if (!ok) return;
    setBusyId(r.id);
    try { await adminCampaignsApi.unapprove(r.id); toast('Se mantiene en revisión'); }
    catch { toast.error('No se pudo actualizar'); }
    finally { setBusyId(null); }
  };

  // Mismo gate que las páginas admin hermanas (AdminEmailsPage/AdminAnalyticsPage):
  // sin rol admin no se pinta nada (la API igual devolvería 403; esto es UX).
  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="bg-red-50 dark:bg-red-500/10 p-4 rounded-md shadow border border-red-200 animate-fade-in-up">
          <div className="flex">
            <div className="flex-shrink-0">
              <LifeBuoy className="h-5 w-5 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Acceso restringido</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>Solo los administradores pueden ver las solicitudes de campañas de correo.</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MailCheck className="h-6 w-6 text-accent" />
          <div>
            <h1 className="text-xl font-semibold text-content">Correos</h1>
            <p className="text-sm text-content-3">Solicitudes: campañas por correo que esperan tu aprobación antes de enviarse.</p>
          </div>
        </div>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-line text-content-2 hover:bg-surface-2 disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
        </button>
      </div>
      <div className="mb-6"><PestanasModulo tabs={CORREOS_TABS} activa="solicitudes" testid="correos-tabs" /></div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-content-3"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-content-3">
          <Inbox className="h-10 w-10 mb-3 opacity-60" />
          <p>No hay solicitudes pendientes.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="bg-surface border border-line rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">🕓 En revisión</span>
                  <span className="text-sm font-medium text-content truncate">{r.name}</span>
                </div>
                <p className="text-sm text-content-2 mt-1 truncate">
                  <b>{r.tenant}</b> · Asunto: {r.email_subject || <span className="text-red-500">(sin asunto)</span>}
                </p>
                <p className="text-xs text-content-3 mt-0.5 flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" /> {r.total_queued} destinatario{r.total_queued === 1 ? '' : 's'}
                  {r.scheduled_at && <> · solicitada {new Date(r.scheduled_at).toLocaleString('es-MX')}</>}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => reject(r)} disabled={busyId === r.id}
                  className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md border border-line text-content-2 hover:bg-surface-2 disabled:opacity-50">
                  <X className="h-4 w-4" /> En revisión
                </button>
                <button onClick={() => approve(r)} disabled={busyId === r.id}
                  className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                  {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Aprobar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
      {dialog}
    </Layout>
  );
};

export default AdminEmailRequestsPage;
